import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import { THEME_PRESETS } from "@/lib/themes";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";


export default function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [orgName, setOrgName] = useState(user?.organization_name || "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [themePreset, setThemePreset] = useState(user?.theme_preset || "dark_teal");
  const [customColors, setCustomColors] = useState(user?.custom_colors || { from: "#0f172a", via: "#1e293b", to: "#0f766e", accent: "#14b8a6" });
  const [themeLoading, setThemeLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(user?.payment_method || "");
  const [paymentAddress, setPaymentAddress] = useState(user?.payment_address || "");
  const [paymentLoading, setPaymentLoading] = useState(false);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    try {
      await api.put(`/user/settings`, { name, email, organization_name: orgName });
      toast.success("Profile updated");
      refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    if (!currentPassword || !newPassword) {
      toast.error("Fill in both password fields");
      return;
    }
    setPasswordLoading(true);
    try {
      await api.put(
        `/user/settings`,
        { current_password: currentPassword, new_password: newPassword }
      );
      toast.success("Password updated");
      setCurrentPassword("");
      setNewPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 py-8 md:py-12 animate-fade-in" data-testid="settings-page">
      <div className="mb-8">
        <p className="text-xs font-medium tracking-widest uppercase text-emerald-600 mb-2">Account</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight" data-testid="settings-title">
          Settings
        </h1>
      </div>

      <Card className="border-border rounded-sm shadow-sm bg-white/80 backdrop-blur-sm mb-6" data-testid="profile-settings-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg font-semibold">Profile</CardTitle>
          <CardDescription>Update your personal information</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleProfileUpdate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="settings-name">Full Name</Label>
              <Input
                id="settings-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="settings-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="settings-email">Email</Label>
              <Input
                id="settings-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="settings-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="settings-org">Organization Name</Label>
              <Input
                id="settings-org"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                placeholder="Your organization or business name"
                data-testid="settings-org-input"
              />
              <p className="text-xs text-muted-foreground">Displayed on your public support page</p>
            </div>
            <Button type="submit" className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white transition-all hover:shadow-lg hover:shadow-emerald-600/20" disabled={profileLoading} data-testid="settings-profile-submit">
              {profileLoading ? "Saving..." : "Save Changes"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Payment Settings */}
      <Card className="border-border rounded-sm shadow-sm bg-white/80 backdrop-blur-sm mb-6" data-testid="payment-settings-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg font-semibold">Payment Settings</CardTitle>
          <CardDescription>Set your preferred withdrawal method and address</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger data-testid="settings-payment-method">
                  <SelectValue placeholder="Select payment method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="paypal">PayPal</SelectItem>
                  <SelectItem value="crypto_btc">Crypto - Bitcoin</SelectItem>
                  <SelectItem value="crypto_eth">Crypto - Ethereum</SelectItem>
                  <SelectItem value="crypto_usdt">Crypto - USDT</SelectItem>
                  <SelectItem value="wise">Wise</SelectItem>
                  <SelectItem value="venmo">Venmo</SelectItem>
                  <SelectItem value="cashapp">Cash App</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="payment-address">Payment Address</Label>
              <Input
                id="payment-address"
                value={paymentAddress}
                onChange={(e) => setPaymentAddress(e.target.value)}
                placeholder={
                  paymentMethod === "bank_transfer" ? "Account number / IBAN" :
                  paymentMethod === "paypal" ? "PayPal email address" :
                  paymentMethod.startsWith("crypto") ? "Wallet address" :
                  paymentMethod === "wise" ? "Wise email" :
                  paymentMethod === "venmo" ? "Venmo username" :
                  paymentMethod === "cashapp" ? "$Cashtag" :
                  "Enter payment address"
                }
                data-testid="settings-payment-address"
              />
            </div>
            <Button
              className="rounded-sm"
              disabled={paymentLoading}
              onClick={async () => {
                setPaymentLoading(true);
                try {
                  await api.put(`/user/settings`, {
                    payment_method: paymentMethod,
                    payment_address: paymentAddress
                  });
                  toast.success("Payment settings saved");
                  refreshUser();
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Failed to save");
                } finally {
                  setPaymentLoading(false);
                }
              }}
              data-testid="settings-payment-submit"
            >
              {paymentLoading ? "Saving..." : "Save Payment Settings"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border rounded-sm shadow-sm bg-white/80 backdrop-blur-sm mb-6" data-testid="password-settings-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg font-semibold">Password</CardTitle>
          <CardDescription>Change your password</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordUpdate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current-password">Current Password</Label>
              <Input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                data-testid="settings-current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                minLength={6}
                data-testid="settings-new-password-input"
              />
            </div>
            <Button type="submit" className="rounded-sm" disabled={passwordLoading} data-testid="settings-password-submit">
              {passwordLoading ? "Updating..." : "Update Password"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Theme Settings */}
      <Card className="border-border rounded-sm shadow-sm bg-white/80 backdrop-blur-sm" data-testid="theme-settings-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg font-semibold">Page Theme</CardTitle>
          <CardDescription>Choose a theme for your public support page</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="theme-presets-grid">
            {Object.entries(THEME_PRESETS).map(([key, t]) => (
              <button
                key={key}
                onClick={() => setThemePreset(key)}
                className={`relative rounded-lg p-3 text-left transition-all duration-200 border-2 ${themePreset === key ? "border-foreground ring-1 ring-foreground/20" : "border-transparent hover:border-border"}`}
                data-testid={`theme-preset-${key}`}
              >
                <div className="h-10 rounded-md mb-2" style={{ background: `linear-gradient(135deg, ${t.from}, ${t.to})` }} />
                <span className="text-xs font-medium">{t.name}</span>
              </button>
            ))}
            <button
              onClick={() => setThemePreset("custom")}
              className={`relative rounded-lg p-3 text-left transition-all duration-200 border-2 ${themePreset === "custom" ? "border-foreground ring-1 ring-foreground/20" : "border-transparent hover:border-border"}`}
              data-testid="theme-preset-custom"
            >
              <div className="h-10 rounded-md mb-2 bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500" />
              <span className="text-xs font-medium">Custom</span>
            </button>
          </div>

          {themePreset === "custom" && (
            <div className="grid grid-cols-2 gap-3 pt-2" data-testid="custom-colors-section">
              <div className="space-y-1">
                <Label className="text-xs">From Color</Label>
                <div className="flex gap-2 items-center">
                  <input type="color" value={customColors.from} onChange={(e) => setCustomColors(c => ({ ...c, from: e.target.value }))} className="h-8 w-8 rounded cursor-pointer" data-testid="custom-color-from" />
                  <Input value={customColors.from} onChange={(e) => setCustomColors(c => ({ ...c, from: e.target.value }))} className="h-8 text-xs font-mono" />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Via Color</Label>
                <div className="flex gap-2 items-center">
                  <input type="color" value={customColors.via} onChange={(e) => setCustomColors(c => ({ ...c, via: e.target.value }))} className="h-8 w-8 rounded cursor-pointer" data-testid="custom-color-via" />
                  <Input value={customColors.via} onChange={(e) => setCustomColors(c => ({ ...c, via: e.target.value }))} className="h-8 text-xs font-mono" />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">To Color</Label>
                <div className="flex gap-2 items-center">
                  <input type="color" value={customColors.to} onChange={(e) => setCustomColors(c => ({ ...c, to: e.target.value }))} className="h-8 w-8 rounded cursor-pointer" data-testid="custom-color-to" />
                  <Input value={customColors.to} onChange={(e) => setCustomColors(c => ({ ...c, to: e.target.value }))} className="h-8 text-xs font-mono" />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Accent Color</Label>
                <div className="flex gap-2 items-center">
                  <input type="color" value={customColors.accent} onChange={(e) => setCustomColors(c => ({ ...c, accent: e.target.value }))} className="h-8 w-8 rounded cursor-pointer" data-testid="custom-color-accent" />
                  <Input value={customColors.accent} onChange={(e) => setCustomColors(c => ({ ...c, accent: e.target.value }))} className="h-8 text-xs font-mono" />
                </div>
              </div>
            </div>
          )}

          {/* Preview */}
          <div className="rounded-lg overflow-hidden" data-testid="theme-preview">
            <div
              className="h-24 flex items-center justify-center"
              style={{
                background: themePreset === "custom"
                  ? `linear-gradient(135deg, ${customColors.from}, ${customColors.via}, ${customColors.to})`
                  : `linear-gradient(135deg, ${(THEME_PRESETS[themePreset] || THEME_PRESETS.dark_teal).from}, ${(THEME_PRESETS[themePreset] || THEME_PRESETS.dark_teal).to})`
              }}
            >
              <span className="text-white/80 text-sm font-medium">Theme Preview</span>
            </div>
          </div>

          <Button
            className="rounded-sm"
            disabled={themeLoading}
            onClick={async () => {
              setThemeLoading(true);
              try {
                await api.put(`/user/settings`, {
                  theme_preset: themePreset,
                  custom_colors: themePreset === "custom" ? customColors : null
                });
                toast.success("Theme saved");
                refreshUser();
              } catch (err) {
                toast.error(err.response?.data?.detail || "Failed to save theme");
              } finally {
                setThemeLoading(false);
              }
            }}
            data-testid="settings-theme-submit"
          >
            {themeLoading ? "Saving..." : "Save Theme"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
